import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostImagesList } from './PostImagesList'
import { Tooltip } from 'components/Tooltip/Tooltip'
import Styles from './PostImagesList.module.scss'
import AddImage from 'almanac-ui/icons/16/AddImage'
import { MediaObject } from 'types'
import { pluralize } from 'utils'
import { Text } from 'almanac-ui/Text/Text'
import { maxFiles, useArticlePostPreviewContext } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { bytesToGb, bytesToMb } from 'blaze-components/MediaUploadModal/utils'
import cx from 'classnames'

interface TextPlatformImagesProps {
  files: MediaObject[]
  setFiles: (files: MediaObject[]) => void
  integration?: ExternalIntegration
  openFileUploadModal: () => void
  maxSize?: number
  maxVideoSize?: number
  provider?: ExternalIntegrationEnum
  readOnly?: boolean
}

export const TextPlatformImages = ({
  files,
  setFiles,
  integration,
  openFileUploadModal,
  maxSize,
  maxVideoSize,
  provider,
  readOnly,
}: TextPlatformImagesProps) => {
  const platform = integration?.provider || provider
  const addMediaLabel = 'Add Images or Videos'

  const {
    state: { uploadingMediaFilesMetadata },
  } = useArticlePostPreviewContext()

  const videoSizeLabel = (platform?: ExternalIntegrationEnum) => {
    if (!maxVideoSize || !platform) return

    if ([ExternalIntegrationEnum.X, ExternalIntegrationEnum.LinkedIn].includes(platform))
      return `${bytesToMb(maxVideoSize)}MB`

    return `${bytesToGb(maxVideoSize)}GB`
  }

  if (readOnly) {
    return (
      <div className={Styles.modalSection}>
        <PostImagesList
          files={files}
          onRemove={(index) => setFiles(files.filter((_, i) => i !== index))}
          readOnly={readOnly}
        />
      </div>
    )
  }

  return (
    <div className={Styles.modalSection}>
      <PostImagesList
        files={files}
        addButton={
          platform ? (
            <Tooltip
              label={`You can only upload ${maxFiles[platform]} ${pluralize(
                'file',
                maxFiles[platform],
              )} for this post.`}
              hide={files.length < maxFiles[platform]}
              placement="top"
            >
              <button
                disabled={files.length >= maxFiles[platform] || uploadingMediaFilesMetadata.length > 0}
                className={cx(Styles.addMediaButton, 'social-media-doc-tour-2')}
                onClick={openFileUploadModal}
              >
                <AddImage size={24} />
                <span>{addMediaLabel}</span>
              </button>
            </Tooltip>
          ) : (
            <button className={cx(Styles.addMediaButton, 'social-media-doc-tour-2')} onClick={openFileUploadModal}>
              <AddImage size={24} />
              <span>{addMediaLabel}</span>
            </button>
          )
        }
        onRemove={(index) => setFiles(files.filter((_, i) => i !== index))}
      />
      <div className={Styles.maxSizes}>
        {maxSize && <Text variant="label">{`Max image size: ${maxSize / 1024 / 1024}MB`}</Text>}
        {maxVideoSize && <Text variant="label">{`Max video size: ${videoSizeLabel(platform)}`}</Text>}
      </div>
    </div>
  )
}
